import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Building2, Download, Mail, Phone, Smartphone } from 'lucide-react';
import { businessInfo } from '../../utils/businessInfo';

const footerLinks = {
    company: [
        { label: 'About Us', href: '/about' },
        { label: 'Contact', href: '/contact' },
        { label: 'Demo', href: '/demo' },
    ],
    legal: [
        { label: 'Privacy Policy', href: '/privacy-policy' },
        { label: 'Terms of Use', href: '/terms' },
        { label: 'Community Guidelines', href: '/community-guidelines' },
        { label: 'Refund Policy', href: '/refund-policy' },
        { label: 'Child Safety', href: '/child-safety' },
        { label: 'Account Deletion', href: '/account-deletion' },
    ],
    support: [
        { label: 'Help Center', href: '/contact' },
        { label: 'Referral Program', href: '/referral-register' },
        { label: 'Safety Support', href: '/community-guidelines' },
    ],
};

export const Footer: React.FC = () => {
    const currentYear = new Date().getFullYear();
    const [showIosNotice, setShowIosNotice] = useState(false);

    const showIosComingSoon = () => {
        setShowIosNotice(true);
        window.setTimeout(() => setShowIosNotice(false), 3200);
    };

    return (
        <footer className="bg-gray-900 text-white">
            {showIosNotice && (
                <div
                    role="status"
                    aria-live="polite"
                    className="fixed bottom-6 left-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 rounded-xl bg-gray-950 px-5 py-4 text-center text-sm font-semibold text-white shadow-2xl"
                >
                    iOS app is coming soon. Android is available now.
                </div>
            )}
            <div className="container-custom">
                {/* Main Footer Content */}
                <div className="grid grid-cols-1 gap-8 py-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-10">
                    {/* Brand Section */}
                    <div className="lg:col-span-5">
                        <Link to="/" className="mb-4 flex items-center space-x-2">
                            <picture>
                                <source srcSet="/assets/app_icon_512_512.webp" type="image/webp" />
                                <img
                                    src="/assets/app_icon_512_512.png"
                                    alt="Swag Shots Logo"
                                    width="40"
                                    height="40"
                                    className="w-10 h-10 rounded-lg"
                                />
                            </picture>
                            <span className="text-xl font-bold text-white">Swag Shots</span>
                        </Link>
                        <p className="max-w-md text-sm leading-6 text-gray-400">
                            Create short videos, go live, and connect with creators across supported regions.
                        </p>

                        {/* Contact Info */}
                        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3 text-sm text-gray-400">
                            <div className="flex items-center gap-2">
                                <Building2 size={16} />
                                <span className="text-gray-300">{businessInfo.operatorName}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Mail size={16} />
                                <a href={`mailto:${businessInfo.email}`} className="hover:text-white transition-colors">{businessInfo.email}</a>
                            </div>
                            <div className="flex items-center gap-2">
                                <Phone size={16} />
                                <a href={`tel:${businessInfo.phoneHref}`} className="hover:text-white transition-colors">{businessInfo.phoneDisplay}</a>
                            </div>
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <button
                                type="button"
                                onClick={showIosComingSoon}
                                className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-700 px-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
                            >
                                <Smartphone size={16} />
                                iOS Soon
                            </button>
                            <a
                                href="https://play.google.com/store/apps/details?id=com.swagshots.app"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex h-10 items-center gap-2 rounded-lg bg-white px-3 text-sm font-semibold text-gray-900 transition-opacity hover:opacity-90"
                            >
                                <Download size={16} />
                                Google Play
                            </a>
                        </div>
                    </div>

                    {/* Company Links */}
                    <div className="lg:col-span-2">
                        <h3 className="mb-3 font-semibold text-white">Company</h3>
                        <ul className="space-y-2.5">
                            {footerLinks.company.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        to={link.href}
                                        className="text-sm text-gray-400 hover:text-white transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal Links */}
                    <div className="lg:col-span-3">
                        <h3 className="mb-3 font-semibold text-white">Legal</h3>
                        <ul className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-1">
                            {footerLinks.legal.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        to={link.href}
                                        className="text-sm text-gray-400 hover:text-white transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Support Links */}
                    <div className="lg:col-span-2">
                        <h3 className="mb-3 font-semibold text-white">Support</h3>
                        <ul className="space-y-2.5">
                            {footerLinks.support.map((link) => (
                                <li key={link.href}>
                                    <Link
                                        to={link.href}
                                        className="text-sm text-gray-400 hover:text-white transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="border-t border-gray-800 py-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        <div className="block text-center text-sm text-gray-400 md:text-left">
                            <p>© {currentYear} Swag Shots. All rights reserved.</p>
                            <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-500">
                                Operated by {businessInfo.operatorName}. GSTIN: {businessInfo.gstin}. Registered address: {businessInfo.registeredAddress}
                            </p>
                        </div>
                        <div className="flex justify-center space-x-5 text-sm">
                            <a
                                href="https://twitter.com/swagshots"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                Twitter
                            </a>
                            <a
                                href="https://instagram.com/swagshots"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                Instagram
                            </a>
                            <a
                                href="https://facebook.com/swagshots"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                Facebook
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};
